import { type KeyboardEvent, type PointerEvent, useRef, useState } from 'react';
import type { SheetWidth } from './sheet-width.ts';

const STEP = 16;
const STEP_LARGE = 64;

export interface SheetResizeHandleProps {
  sheet: SheetWidth;
  /** Accessible name, e.g. "Resize drawer" or "Resize panel". */
  label: string;
}

/**
 * Vertical splitter on the left edge of a right-side sheet (shown from `md`). The value is the sheet width, so
 * dragging or pressing ArrowLeft moves the edge left and widens the sheet; Shift takes larger steps, Home/End jump to
 * the min/max width and a double click restores the default. Render it as the first child of the sheet root, which
 * must be a containing block (`fixed` or `relative`).
 */
export function SheetResizeHandle({ sheet, label }: SheetResizeHandleProps) {
  const drag = useRef<{ pointerId: number; startX: number; startWidth: number; last: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startWidth: sheet.width, last: sheet.width };
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    d.last = d.startWidth + (d.startX - e.clientX);
    sheet.set(d.last, { persist: false });
  };
  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    sheet.set(d.last);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? STEP_LARGE : STEP;
    let next: number | undefined;
    if (e.key === 'ArrowLeft') next = sheet.width + step;
    else if (e.key === 'ArrowRight') next = sheet.width - step;
    else if (e.key === 'Home') next = sheet.min;
    else if (e.key === 'End' && Number.isFinite(sheet.max)) next = sheet.max;
    if (next === undefined) return;
    e.preventDefault();
    sheet.set(next);
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: an interactive window splitter; <hr> cannot take focus or a value
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={sheet.width}
      aria-valuemin={sheet.min}
      {...(Number.isFinite(sheet.max) ? { 'aria-valuemax': sheet.max } : {})}
      tabIndex={0}
      data-testid="sheet-resize"
      data-dragging={dragging ? 'true' : undefined}
      title="Drag to resize. Double-click to reset."
      className="group absolute inset-y-0 left-0 z-10 hidden w-3 cursor-col-resize touch-none select-none justify-start rounded-r-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus md:flex"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onDoubleClick={sheet.reset}
      onKeyDown={onKeyDown}
    >
      <span
        aria-hidden
        className="h-full w-px rounded-full bg-line transition-[width,background-color] duration-150 group-hover:w-[3px] group-hover:bg-accent group-focus-visible:w-[3px] group-focus-visible:bg-accent group-data-[dragging=true]:w-[3px] group-data-[dragging=true]:bg-accent"
      />
    </div>
  );
}
